import { useInkind } from '@gik/api/inkinds/inkind';
import { useEnvStore } from '@gik/core/store/EnvStore';
import { useBemCN } from '@gik/core/utils/bemBlock';
import DateTimeUtils from '@gik/core/utils/DateTimeUtils';
import { copyWithNotification } from '@gik/core/utils/LinkUtils';
import noop from '@gik/core/utils/noop';
import { createFacebookShareUrl, createTwitterShareUrl, getUserUpdateLink } from '@gik/core/utils/ShareUtils';
import withComponentErrorBoundary from '@gik/core/utils/withComponentErrorBoundary';
import { translationKeys } from '@gik/inkind-page/i18n/en';
import type InkindUserUpdate from '@gik/inkind-page/models/InkindUserUpdate';
import { dateDisplayFormatFull, monthAndDayDisplayFormat, weekDayDisplayFormat, yearDisplayFormat } from '@gik/l10n';
import { StreamReactions } from '@gik/stream/components/Posts/StreamReactions';
import type { UserUpdateActivity } from '@gik/stream/types/Stream';
import type { StreamParticipant } from '@gik/stream/types/StreamParticipant';
import { Button } from '@gik/ui/Button';
import type { IButtonProps } from '@gik/ui/Button/ButtonProps';
import { LayoutAside } from '@gik/ui/LayoutAside';
import { LineClamp } from '@gik/ui/LineClamp';
import { Separator } from '@gik/ui/Separator';
import { AnimatedDivBone } from '@gik/ui/SkeletonLoader';
import { Facebook } from '@gik/ui/SvgIcon/BrandIcons/Facebook';
import { Twitter } from '@gik/ui/SvgIcon/BrandIcons/Twitter';
import { SvgIcon } from '@gik/ui/SvgIcon/SvgIcon';
import { Text } from '@gik/ui/Text/Text';
import { UI } from '@gik/ui/UIManager';
import type {
  ILightboxImageMediaType,
  ILightboxVideoMediaType,
  LightboxMediaType,
} from '@gik/ui/UIManager/UILightboxTypes';
import { LightboxMedia } from '@gik/ui/UIManager/UILightboxTypes';
import PencilIcon from '@heroicons/react/outline/PencilIcon';
import LinkIcon from '@heroicons/react/solid/LinkIcon';
import PlayIcon from '@heroicons/react/solid/PlayIcon';
import CloseIcon from '@heroicons/react/solid/XIcon';
import moment from 'moment';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Debug } from '@gik/core/utils/asPlaceholderReactNode';

export interface IInkindUserUpdateTimelineCardProps {
  update: InkindUserUpdate;
  debugActivity?: UserUpdateActivity;
  participants: StreamParticipant[];
  canEdit: boolean;
  inkindRouteId: string;
  onReaction: () => void;
  updateActivity: UserUpdateActivity;
  onEdit?(update: InkindUserUpdate): void;
  onDelete?(update: InkindUserUpdate): void;
}

const blockName = 'inkind-user-update-timeline-card';

export function fixUserUpdatesLegacyImageUrl(url: string) {
  if (url?.startsWith('/images')) {
    return useEnvStore.getState().BASE_URL + url.substring(1);
  }
  return url;
}

function InkindUserUpdateTimelineCardComp({
  update,
  debugActivity,
  canEdit,
  inkindRouteId,
  participants,
  updateActivity,
  onEdit = noop,
  onDelete = noop,
  onReaction = noop,
}: IInkindUserUpdateTimelineCardProps) {
  const bem = useBemCN(blockName);
  const { t } = useTranslation();

  const { data: inkindPage } = useInkind(inkindRouteId);
  const updateLink = React.useMemo(() => update && encodeURIComponent(getUserUpdateLink(update)), [update]);

  const baseShareButtonsOptions: Partial<IButtonProps> = {
    variant: 'default-plain',
    circle: true,
  };

  function openAttachmentLightbox(index: number) {
    const media: LightboxMediaType[] = update.media?.map(value => {
      switch (value.type) {
        case 'photo':
          return {
            mediaType: LightboxMedia.IMAGE,
            url: fixUserUpdatesLegacyImageUrl(value.url),
            caption: value.caption,
          } as ILightboxImageMediaType;
        case 'video':
          return {
            mediaType: LightboxMedia.VIDEO,
            url: value.url,
            caption: value.caption,
          } as ILightboxVideoMediaType;
      }
    });

    return UI.lightbox(media, {}, index);
  }

  return (
    <LayoutAside {...bem()} id={`timeline-card-${update?.id}`}>
      <main>
        <LineClamp lines={2} showButton={false} fadeOut={false} {...bem('title')} tooltip>
          <Text>{update.title}</Text>
        </LineClamp>

        <p {...bem('updated-by')}>
          Updated by <Text>{update.organiserFullName}</Text>
        </p>
        <div {...bem('share-buttons')}>
          <Button
            {...baseShareButtonsOptions}
            type={'button'}
            tooltip="Copy link"
            onClick={() =>
              copyWithNotification(
                decodeURIComponent(updateLink),
                t(translationKeys.InkindUserUpdateTimelineCardLinkCopiedNotification)
              )
            }
          >
            <SvgIcon Icon={LinkIcon} />
          </Button>

          <Button
            {...baseShareButtonsOptions}
            externalLink
            href={createFacebookShareUrl(updateLink)}
            tooltip={t(translationKeys.InkindUserUpdateTimelineCardShareFacebook).toString()}
          >
            <SvgIcon Icon={Facebook} />
          </Button>

          <Button
            {...baseShareButtonsOptions}
            externalLink
            href={createTwitterShareUrl(updateLink)}
            tooltip={t(translationKeys.InkindUserUpdateTimelineCardShareTwitter).toString()}
          >
            <SvgIcon Icon={Twitter} />
          </Button>
        </div>

        <LineClamp lines={10}>
          <Text parseUrls>{update.description}</Text>
        </LineClamp>

        {update.media?.length > 0 && (
          <>
            <p {...bem('attachments-label')}>
              {t(translationKeys.InkindUserUpdateTimelineCardAttachmentLabel).toString()}
            </p>

            <Separator size={'xs'} type={'dashed'} />

            <div {...bem('attachments-wrapper')}>
              {update.media.map((media, index) => (
                <div
                  key={media.id}
                  {...bem('attachment-wrapper', [{ [media.type]: true }])}
                  onClick={() => openAttachmentLightbox(index)}
                >
                  <AnimatedDivBone {...bem('thumbnail-skeleton')} />
                  <img alt="Thumbnail" src={fixUserUpdatesLegacyImageUrl(media.thumbnailUrl)} {...bem('attachment')} />
                  {media.type === 'video' && (
                    <div {...bem('play-icon-wrapper')}>
                      <SvgIcon Icon={PlayIcon} />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        {inkindPage?.allowDiscussions && (
          <StreamReactions
            activity={updateActivity}
            isPageOwnerOrOrganizer={canEdit}
            onReaction={onReaction}
            participants={participants}
          />
        )}
      </main>
      <aside {...bem('aside')}>
        <Separator {...bem('line')} type={'solid'} thickness={'normal'} direction={'vertical'} />
        <div {...bem('aside-content')}>
          <div
            {...bem('date')}
            title={moment(update.publishedDate, DateTimeUtils.DateFormat).format(dateDisplayFormatFull)}
          >
            {debugActivity && (
              <Debug
                style={{
                  position: 'absolute',
                  right: '75px',
                }}
                data={{ time: debugActivity.time, updatedTime: debugActivity.updatedTime }}
              />
            )}
            <p {...bem('week-day')}>
              {moment(update.publishedDate, DateTimeUtils.DateFormat).format(weekDayDisplayFormat).toUpperCase()}
            </p>
            <p {...bem('month-and-day')}>
              {moment(update.publishedDate, DateTimeUtils.DateFormat).format(monthAndDayDisplayFormat)}
            </p>
            <p {...bem('year')}>{moment(update.publishedDate, DateTimeUtils.DateFormat).format(yearDisplayFormat)}</p>
          </div>
          {canEdit && (
            <Button
              circle
              size={'sm'}
              variant={'secondary'}
              onClick={() => onEdit(update)}
              tooltip={t(translationKeys.EditUpdateButtonLabel).toString()}
            >
              <SvgIcon Icon={PencilIcon} />
            </Button>
          )}
          {canEdit && (
            <Button
              circle
              size={'sm'}
              variant={'danger'}
              onClick={() => onDelete(update)}
              tooltip={t(translationKeys.DeleteUpdateButtonLabel).toString()}
            >
              <SvgIcon Icon={CloseIcon} />
            </Button>
          )}
        </div>
      </aside>
    </LayoutAside>
  );
}

export const InkindUserUpdateTimelineCard = withComponentErrorBoundary(InkindUserUpdateTimelineCardComp);
